import { Timestamp } from 'firebase/firestore'

// Diagnosis document with soft delete support
export type DiagnosisDocument =
  | string // legacy format
  | { url: string; uploadedAt: string } // current format
  | {
      url: string
      uploadedAt: string
      deleted?: boolean
      deletedAt?: string // ISO date string
      deletedBy?: string // admin full name
    }

// Type guard: Check if document is in object format (not legacy string)
export function isDiagnosisDocumentObject(doc: DiagnosisDocument): doc is {
  url: string
  uploadedAt: string
  deleted?: boolean
  deletedAt?: string
  deletedBy?: string
} {
  return typeof doc === 'object' && doc !== null && 'url' in doc
}

// Type guard: Check if document is deleted
export function isDocumentDeleted(doc: DiagnosisDocument): boolean {
  if (typeof doc === 'string') return false
  return 'deleted' in doc && doc.deleted === true
}

// Diagnosis document deletion record for history
export type DiagnosisDocumentDeletion = {
  documentNumber: number
  deletedBy: string
  deletedAt: string // ISO date string
}

// Role change record for history
export type RoleChange = {
  role: Role
  action: 'added' | 'removed'
  changedBy: string // Full name of admin who made the change
  changedAt: string // ISO date string
}

export type Role = 'admin' | 'patient' | 'superadmin' | 'tester' | 'researcher'

export const roleTitles: Record<Role, string> = {
  admin: 'Admin',
  patient: 'User',
  superadmin: 'Super-Admin',
  tester: 'Tester',
  researcher: 'Researcher',
}

export const shortRoleTitles: Record<Role, string> = {
  admin: 'A',
  patient: 'U',
  superadmin: 'SA',
  tester: 'T',
  researcher: 'R',
}

export type StageStatus = 'unstarted' | 'started' | 'complete'

// New type for stage data with multiple completions
export type StageData = {
  status: StageStatus
  completions?: ({ month: number; year: number } | null)[]
}

export type Profile = {
  firstName: string
  lastName: string
  searchFirstName: string
  searchLastName: string
  birthDate: string
  gender: Gender
  diagnosis: Diagnosis
  race: string
  education: string
  diagnosisDate: string
  diagnosisVerified: boolean
  diagnosisVerificationDate?: string
  diagnosisVerifiedBy?: string
  diagnosisDateVerified?: boolean
  diagnosisDateVerificationDate?: string
  diagnosisDateVerifiedBy?: string
  diagnosisDocumentation?: DiagnosisDocument[]
  firstSymptomsDate: string
  height: string
  address: string
  city: string
  state: string
  country: string
  postalCode: string
  phoneNumber: string
  phoneCountryCode?: string // ISO country code (e.g., "US", "GB", "AU")
  emergencyContactFirstName: string
  emergencyContactLastName: string
  emergencyContactRelationship?: string
  emergencyContactRelationshipOther?: string
  emergencyContactEmail: string
  emergencyContactPhone: string
  emergencyContactPhoneCountryCode?: string // ISO country code (e.g., "US", "GB", "AU")
  agreedToProfileCheckbox: boolean
  isDeceased?: boolean
  yearOfDeath?: string
  causeOfDeath?: string
}

export type AdditionalDetails = {
  alsOnset: string
  alsOnsetClassification?: 'sporadic' | 'familial'
  familialGene?: string
  contributingFactors: ContributingFactors
  symptoms: Symptom[]
  isInClinicalTrial: string
  clinicalTrials?: ClinicalTrial[]
  futureTrialAgreement: boolean
  hasParticipatedInClinicalTrials?: boolean
  occupations?: Occupation[]
  isComplete?: boolean
}

export type MedicalDetails = {
  medicalHistory: MedicalData[]
  prescriptionHistory: PrescriptionData[]
  injuryHistory: InjuryData[]
  surgeryHistory: SurgeryData[]
  childhoodVaccineHistory: ChildhoodVaccineData[]
  adultVaccineHistory: AdultVaccineData[]
  allergyHistory: AllergyData[]
}

export type DentalDetails = {
  currentNumAmalgamFillings: number
  totalNumAmalgamFillings: number
  ageFirstAmalgam: number
  ageLastAmalgamRemoved: number
  currentNumRootCanals: number
  totalNumRootCanals: number
  ageFirstRootCanal: number
  ageLastRootCanalRemoved: number
  totalNumTeethRemoved: number
  ageFirstToothRemoved: number
  checkedCavitations: boolean
  numCavitations: number
  currentNumImplants: number
  totalNumImplants: number
  ageFirstImplant: number
  ageLastImplantRemoved: number
  isSaved?: boolean
}

export type MedicalCondition = {
  conditionCategory:
    | 'various'
    | 'neurological'
    | 'heart'
    | 'gastro'
    | 'endocrine'
    | 'respiratory'
    | 'viral'
    | 'fungal'
    | 'parasites'
  conditionValue: string
  checked: boolean
  ageDiagnosed: number
  ageEnded: number | null
  intermittent: boolean
  estimated: number
}

export type Diagnosis =
  | 'als'
  | 'pls'
  | 'als-probable'
  | 'administrator'
  | 'medical'
  | 'nonprofit'
  | 'other'

export const diagnosisTitles: Record<Diagnosis, string> = {
  als: 'ALS/MND',
  pls: 'PLS',
  'als-probable': 'Probable ALS/MND/PLS',
  administrator: 'PALS Support Team Member',
  medical: 'Medical Professional or Researcher',
  nonprofit: 'Nonprofit Team Member',
  other: 'Other Diganosis',
}
export const shortDiagnosisTitles: Record<Diagnosis, string> = {
  als: 'ALS',
  pls: 'PLS',
  'als-probable': 'Prob-ALS',
  administrator: 'PSTM',
  medical: 'MP',
  nonprofit: 'NPTM',
  other: 'Other Diagnosis',
}

export type Gender = 'male' | 'female' | 'other'

export type UnitSystem = 'metric' | 'imperial'

export type QuestionnaireInterface = 'new' | 'classic'

export type ContributingFactors = Partial<{
  primary: string | undefined
  secondary: string | undefined
  tertiary: string | undefined
  quaternary: string | undefined
  quinary: string | undefined
}>

export type Symptom = {
  area: string
  side: 'left' | 'right' | 'both' | undefined | ''
}

export type ClinicalTrial = {
  id: string
  study: string
  startDate: string
  endDate?: string
  group?: 'Interventional' | 'Placebo' | ''
}

export type StressLevel =
  | 'High Stress'
  | 'Medium High Stress'
  | 'Medium Stress'
  | 'Medium Low Stress'
  | 'Low Stress'

export type Occupation = {
  id: string
  occupationName: string
  startDate: string
  endDate?: string
  stressLevel: StressLevel
}

export type PrescriptionData = {
  id: string
  drug?: string
  category?: string
  ageStarted: number | null
  ageStopped: number | null
  usageType: 'Intermittent' | 'Constant'
  totalYears: number | null
}

export type MedicalData = {
  id: string
  condition: string | null
  ageDiagnosed?: number | null | ''
  ageEnded?: number | null | ''
  frequency?: 'intermittent' | 'constant' | null
  estimatedYears?: number | null | ''
}

export type InjuryData = {
  id: string
  injury: string | null
  ageOne: number | null
  ageTwo: number | null
  ageThree: number | null
  ageFour: number | null
  side?: 'left' | 'right' | 'both' | null
}

export type SurgeryData = {
  id: string
  surgery: string | null
  ageOne: number | null
  ageTwo: number | null
  ageThree: number | null
  ageFour: number | null
  side?: 'left' | 'right' | 'both' | null
}

export type ChildhoodVaccineData = {
  id: string
  vaccine: string | null
  estimatedNumber: number | null
  ageOne: number | null
  ageTwo: number | null
  ageThree: number | null
}

export type AdultVaccineData = {
  id: string
  vaccine: string | null
  ageOne: number | null
  ageTwo: number | null
  estimatedNumber: number | null
}

export type AllergyData = {
  id: string
  allergy: string | null
  ageDiagnosed?: number | null | ''
  ageEnded?: number | null | ''
  severity?: 'mild' | 'moderate' | 'severe' | null
  estimatedYears?: number | null | ''
}

export type ExposureData = {
  id: string
  condition: string
  ageDiagnosed: number | null
  ageEnded: number | null
  estimatedYears: number | null
}

export type StudyStatus =
  | 'Participant'
  | 'Withdrawn'
  | 'Completed'
  | 'Applicant'

export interface StudyData {
  name: string
  status: StudyStatus
  startDate?: string
  endDate?: string
  reason?: string
}

export interface StudyChangeLogEntry {
  studyName: string
  status: StudyStatus
  date: string
  reason?: string
}

export type DiagnosisChange = {
  from: Diagnosis
  to: Diagnosis
  date: string
}

export default interface FirestoreUser {
  email: string
  roles?: Role[]
  createdAt: Timestamp
  unitSystem?: UnitSystem
  questionnaireInterface?: QuestionnaireInterface
  profile?: Profile
  additionalDetails?: AdditionalDetails
  medicalConditions?: MedicalCondition[]
  medicalDetails?: MedicalDetails
  dentalDetails?: DentalDetails
  patientId?: string
  stages?: {
    [stageId: string]: StageStatus | StageData
  }
  bootCamps?: Partial<
    Record<
      number,
      {
        startedAt: {
          month: number
          year: number
        }
        completedAt: {
          month: number
          year: number
        } | null
        // New field for multiple completions - supports backward compatibility
        completions?: ({ month: number; year: number } | null)[]
      } | null
    >
  >
  essentialsCourses?: Partial<
    Record<
      number,
      {
        startedAt: {
          month: number
          year: number
        }
        completedAt: {
          month: number
          year: number
        } | null
        // New field for multiple completions - supports backward compatibility
        completions?: ({ month: number; year: number } | null)[]
      } | null
    >
  >
  lockedDate?: string
  studies?: (string | StudyData)[] // Support both old string format and new object format
  studyChangeLogs?: StudyChangeLogEntry[]
  archived?: boolean
  diagnosisChangeHistory?: DiagnosisChange[]
  diagnosisDocumentDeletions?: DiagnosisDocumentDeletion[]
  roleChangeHistory?: RoleChange[]
  // Email verification fields
  emailVerification?: {
    otpHash: string
    expiresAt: Timestamp
    attempts: number
    lastSentAt: Timestamp
  }
  emailVerified?: boolean
}
